import { companyPath, contactPath, groupPath } from 'helper/recordPaths';
import ListIdentity from 'components/record/ListIdentity';
import { Building2Icon, CalendarDaysIcon, ClipboardListIcon, Mail, NetworkIcon, Pencil, SendIcon } from 'assets/icons';
import EmployeeAvatar from 'components/assign/EmployeeAvatar';
import LabelledButton from 'components/button';
import CardIconButton from 'components/card/CardIconButton';
import CompactEmptyState from 'components/EmptyState/CompactEmptyState';
import CopyToClipboard from 'components/copyToClipboard';
import Button from 'components/crm/ui/Button';
import useModal from 'components/modal/useModal';
import RecordActivity from 'components/record/activity/RecordActivity';
import { callAction } from 'components/record/callAction';
import ConfirmDialog from 'components/record/ConfirmDialog';
import ContactEditDialog from 'components/record/ContactEditDialog';
import { apiErrorText, errorCodeOf } from 'helper/apiErrorText';
import { buildActivity } from 'components/record/activity/activityItems';
import { AmbassadorBadge } from 'components/record/ContactRow';
import QuickActions from 'components/record/QuickActions';
import { RecordField } from 'components/record/RecordFields';
import RecordIdentity from 'components/record/RecordIdentity';
import MemberCompanyCard, { ContactCompanyCard } from 'components/record/MemberCompanyCard';
import NextActionCard from 'components/record/NextActionCard';
import RecordLayout, { RecordLayoutSkeleton, RecordSection, RecordTabs } from 'components/record/RecordLayout';
import useOpenPanel from 'components/record/useOpenPanel';
import { EDIT_SCOPES } from 'components/SlideOver/dataSlideOver/data/slideOverCompany/CompanyContactEditView';
import { memberTarget, seedMember } from 'components/SlideOver/dataSlideOver/data/slideOverCompany/slideOverCompany';
import { seedContactCrmFields } from 'components/SlideOver/dataSlideOver/data/slideOverCompany/useContactCrmFields';
import useSlideOver from 'components/SlideOver/useSlideOver';
import { companyRoleLine } from 'constants/companyRoles';
import { COMMERCIAL_ROLE, SUPER_ADMIN_ROLE } from 'helper/roles';
import { useOnChange } from 'hooks/useChangeSignal';
import UseContext from 'hooks/useContext';
import Usefetchdata from 'hooks/usefetchdata';
import useAppointmentPanel from 'hooks/useAppointmentPanel';
import useCrmContext from 'hooks/useCrmContext';
import useLatestCall from 'hooks/useLatestCall';
import useRecordChanged from 'hooks/useRecordChanged';
import useRecordPanels from 'hooks/useRecordPanels';
import useTaskPanels from 'hooks/useTaskPanels';
import useTasksChanged from 'hooks/useTasksChanged';
import useText from 'hooks/useText';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { X as XIcon } from 'lucide-react';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import crmApi from 'server/crm/crmApi';
import { contactNextAction, fullName, nextActionView } from './recordItems';

// The refusals that mean the contact is gone, not that the page failed.
const MISSING_CODES = ['CONTACT_NOT_FOUND', 'INVALID_ID', 'GROUP_NOT_FOUND', 'COMPANY_NOT_FOUND'];

/**
 * The page of a contact, on the model of a company's: who they are, how to
 * reach them, their lead status, every company (and group) they are at, and
 * their activity in all of them (api contact/record), at `/contact/:contactId`:
 * an account's id or a contact's without one; any other id (a former
 * `hs-<id>` address) is refused INVALID_ID, « contact introuvable ». The
 * company or group it belongs to (back, notes, invitation) is the record's
 * `owner`, read from its memberships, not from the address.
 */
const ContactPage = () => {
  const { contactId } = useParams();
  const navigate = useNavigate();
  const { text, language } = useText('record');
  const { text: activityText } = useText('recordActivity');
  const { call, ready } = useCrmContext();
  // Only the last read lands: a newer one cancels it, which is no error.
  const latest = useLatestCall();
  const { setModalData, setObjID, setTarget } = UseContext();
  const { userData } = Usefetchdata();
  const { toggleSlideOver } = useSlideOver();
  const openPanel = useOpenPanel();
  const { toggle } = useModal();
  const { openRecordForm } = useRecordPanels();
  const { openTask, openTaskForm } = useTaskPanels();
  const { openAppointment } = useAppointmentPanel();
  const [record, setRecord] = useState(null);
  const [failed, setFailed] = useState(null);
  const [tab, setTab] = useState('activity');
  // The company the person is taken off, waiting for its confirmation.
  const [removal, setRemoval] = useState(null);
  const [removalBusy, setRemovalBusy] = useState(false);
  const [editingContact, setEditingContact] = useState(false);

  const load = useCallback(
    () =>
      latest(call(crmApi.getContactRecord, { contactId })).then(
        (next) => {
          setRecord(next);
          setFailed(null);
        },
        // A refusal of the api is worded here, never with its own message.
        (error) =>
          setFailed(
            MISSING_CODES.includes(errorCodeOf(error))
              ? { title: text('contactMissing'), description: text('contactMissingText') }
              : { title: text('contactLoadFailed'), description: text('groupLoadFailedText') },
          ),
      ),
    [call, latest, contactId, text],
  );
  useEffect(() => {
    if (ready) load();
  }, [load, ready]);
  useRecordChanged(load);
  useTasksChanged(load);
  useOnChange('employee_created', load);
  // A member taken off a company from its own panel.
  useOnChange('employee_deleted', load);

  const team = useMemo(() => record?.team ?? [], [record]);
  const nameOf = useCallback(
    (personId) => {
      const person = team.find((member) => member.id === personId);
      return person ? fullName(person) : text('someone');
    },
    [team, text],
  );
  const name = record ? fullName(record.person) : '';
  const items = useMemo(
    () => (record ? buildActivity(record.history, { now: new Date(), text: activityText, nameOf, contact: name }) : []),
    [record, activityText, nameOf, name],
  );

  if (failed) {
    return (
      <RecordLayout side={null}>
        <CompactEmptyState
          glyph="searchX"
          title={failed.title}
          description={failed.description}
          action={{ label: text('backCompanies'), onClick: () => navigate('/company') }}
        />
      </RecordLayout>
    );
  }
  if (!record) return <RecordLayoutSkeleton kpis={0} tabs={3} />;

  const { person, owner, memberships } = record;
  const ownerPath = (place) => (place.kind === 'group' ? groupPath(place.id) : companyPath(place.id));
  const target = owner.kind === 'group' ? { groupId: owner.id } : { companyId: owner.id };
  const me = { id: person.id, name, email: person.email, phone: person.phone };
  // A note is linked to the contact itself: its account and its company.
  const noteContact = { id: person.id, name, companyId: owner.kind === 'company' ? owner.id : null };
  // A call, an e-mail or an appointment names the person, not only their
  // name (JBWEB-1427): their account, else their contact at this record.
  const ownerPlace = memberships.find((place) => place.kind === owner.kind && place.id === owner.id);
  const ownContactId = ownerPlace?.contactId ?? (person.kind === 'contact' ? person.id : null);
  const contactRef = person.account
    ? { userId: person.id }
    : ownContactId
      ? { contactId: ownContactId, ...(person.email && { email: person.email }) }
      : null;
  const openForm = (kind, extra = {}) =>
    openRecordForm(kind, {
      ...target,
      recordName: owner.name,
      contacts: [me],
      contactName: name,
      ...(kind === 'note' && { contact: noteContact }),
      ...(contactRef && ['call', 'email', 'event'].includes(kind) && { contactRef }),
      ...extra,
    });
  const add = (kind) => {
    // The task form finds the person by their ref: a namesake at the same record would otherwise be picked.
    if (kind === 'task')
      openTaskForm({ defaults: { type: 'todo', ...target, contactName: name, ...(contactRef && { contactRef }), scoped: true } });
    else openForm(kind === 'all' ? 'note' : kind);
  };
  const openItem = (ref) => {
    // A client's action on a mission opens that mission.
    if (ref.kind === 'client' && ref.missionId) return openPanel('get_mission', ref.missionId);
    if (ref.kind === 'task') openTask(ref.id);
    // A note on a Wildix call stays linked to the contact (and its company), not to the company alone.
    if (ref.kind === 'phoneCall') openForm('note', { phoneCallId: ref.id });
    if (ref.kind === 'client') navigate(`${companyPath(ref.companyId ?? owner.id)}?onglet=${ref.tab}`);
  };
  // As « Inviter » of the company's contacts (CompanyPage): the company's own
  // contact is invited with its id.
  const inviteAt = (place) => {
    const prefill = { email: person.email, firstName: person.firstname, lastName: person.lastname, phone: person.phone };
    setModalData({ idCompany: place.id, prefill, companyContactId: place.contactId });
    toggle('add_company_member');
  };
  // A person without an account is invited at each company they are a
  // contact of, a prospect as a client.
  const canInviteAt = (place) => place.kind === 'company' && !place.deactivated && !person.account && Boolean(place.contactId);
  // As the group's own page allows (crmGroupRights of the api).
  const canEditGroupContacts = [SUPER_ADMIN_ROLE, COMMERCIAL_ROLE].includes(Number(userData?.role));
  // A company's contacts without an account follow the same rule (company/contact/delete).
  const canRemoveContact = canEditGroupContacts;
  // « Retirer » deactivates an account at that company (it stays listed,
  // « Désactivé », and can come back), or deletes a contact without one.
  const removable = (place) =>
    place.kind === 'company' && !place.deactivated && (place.account ? true : Boolean(place.contactId) && canRemoveContact);
  const confirmRemoval = () => {
    const place = removal;
    setRemovalBusy(true);
    const removed = place.account
      ? call(crmApi.deactivateCompanyMember, { companyId: place.id, userId: person.id })
      : call(crmApi.deleteCompanyContact, { companyId: place.id, contactId: place.contactId });
    removed
      .then(
        () => {
          toast.success(text(place.account ? 'contactDeactivatedSaved' : 'contactRemovedSaved', { company: place.name }));
          // The contact the page's address names is gone: the page moves to
          // another of their places, else back to that company.
          if (!place.account && place.contactId === contactId) {
            const next = memberships.find((other) => other !== place && other.contactId);
            navigate(next ? contactPath(next.contactId) : companyPath(place.id), { replace: true });
            return;
          }
          load();
        },
        (error) => toast.error(apiErrorText(error, { language, text, group: place.account ? 'memberErrors' : 'companyContactErrors' })),
      )
      .finally(() => {
        setRemovalBusy(false);
        setRemoval(null);
      });
  };
  const reactivateAt = (place) =>
    call(crmApi.reactivateCompanyMember, { companyId: place.id, userId: person.id }).then(
      () => {
        toast.success(text('contactReactivatedSaved', { company: place.name }));
        load();
      },
      (error) => toast.error(apiErrorText(error, { language, text, group: 'memberErrors' })),
    );
  // A member is edited in a company's panel, on them: the person themselves
  // (« Informations »), or their place at that company (« Entreprise »). The
  // panel opens on what this page already shows, then reads the rest.
  const editMemberAt = (place, scope) => () => {
    seedMember(place.id, {
      role: place.role,
      user: {
        _id: person.id,
        firstname: person.firstname ?? '',
        lastname: person.lastname ?? '',
        email: person.email ?? '',
        phone: person.phone ?? '',
        validation: place.account === 'pending' ? 0 : 1,
      },
    });
    seedContactCrmFields(person.id, { position: person.position, leadStatus: person.leadStatus });
    setObjID(place.id);
    setTarget(memberTarget(person.id, scope));
    toggleSlideOver('get_company');
  };
  // The pencil of « Informations »: a person without an account is edited in
  // all their companies and groups at once (contact/save, 500 and 510 only);
  // anyone else at a group keeps the group's contact form; a member opens
  // « Modifier le contact » on the person (name, e-mail, position, lead
  // status).
  const editInformation =
    person.kind === 'contact'
      ? canEditGroupContacts && (() => setEditingContact(true))
      : owner.kind === 'group'
        ? canEditGroupContacts && (() => openForm('groupContact', { contact: person, afterDelete: ownerPath(owner) }))
        : person.account && editMemberAt(ownerPlace ?? { id: owner.id, role: person.role, account: person.account }, EDIT_SCOPES.person);
  const groups = memberships.filter((place) => place.kind === 'group').length;
  // The companies where the person has an account: one card each (role, perimeters, notifications).
  const nextAction = contactNextAction(record.history, new Date());
  const next = nextActionView(nextAction, { text, contactName: name, language, now: new Date() });
  const nextButtons =
    next?.kind === 'task'
      ? {
          primary: { label: text('openTask'), onClick: () => openTask(nextAction.id) },
          secondary: { label: text('postpone'), onClick: () => openTaskForm({ taskId: nextAction.id }) },
        }
      : next && {
          primary: { label: text('outcome'), onClick: () => openForm('note', { outcome: true }) },
          // The same appointment moved: its form, every field filled.
          secondary: {
            label: text('postpone'),
            onClick: () => (nextAction.id ? openAppointment(nextAction.id, { edit: true }) : openForm('event')),
          },
        };
  // A place deactivated keeps its settings stored and is shown last, with a badge.
  const byActiveFirst = (a, b) => Number(Boolean(a.deactivated)) - Number(Boolean(b.deactivated));
  const accountPlaces = memberships.filter((place) => place.kind === 'company' && place.account).sort(byActiveFirst);
  const placeDetail = (place) => {
    if (place.kind === 'group') return [place.position || text('noPosition'), text('groupContact')].join(' · ');
    // At a company: the role there (the position is the person's, above), then
    // where the account stands. A contact without an account only has the role
    // they will be invited with, not one they hold.
    const role = place.role && place.account ? companyRoleLine(language, place.role) : '';
    const account = place.deactivated
      ? text('contactDeactivated')
      : place.account === 'pending'
        ? text('pending')
        : !place.account
          ? text('notOnJobbiz')
          : '';
    return [role, account].filter(Boolean).join(' · ');
  };

  // Invite at a company, remove from a company: on its line of the side and
  // on its card of « Entreprises », grouped 4 px apart as a contact row's
  // actions (ContactRow). On the card, « Inviter » is written, as on the
  // company's contacts (CompanyContacts).
  const placeActions = (place, { labelled = false } = {}) =>
    (canInviteAt(place) || removable(place)) && (
      <span className="flex shrink-0 items-center gap-1">
        {canInviteAt(place) &&
          (labelled ? (
            <LabelledButton
              color="SECONDARY"
              size="SM"
              aria-label={text('inviteAt', { company: place.name })}
              onClick={() => inviteAt(place)}
            >
              <SendIcon wh={16} color="currentColor" />
              <span>Inviter</span>
            </LabelledButton>
          ) : (
            <CardIconButton label={text('inviteAt', { company: place.name })} icon={<SendIcon wh={18} color="#374151" />} onClick={() => inviteAt(place)} />
          ))}
        {removable(place) && (
          <CardIconButton
            label={text('contactRemoveFrom', { company: place.name })}
            ariaLabel={text('contactRemoveFromLabel', { name, company: place.name })}
            icon={<XIcon size={18} color="#374151" />}
            onClick={() => setRemoval(place)}
          />
        )}
      </span>
    );

  // Roles, perimeters and notifications are a Jobbiz account's, by company:
  // none to set before the invitation. Its companies all the same.
  const notOnJobbiz = (
    <div className="flex flex-col gap-2 pb-6">
      {memberships
        .filter((place) => place.kind === 'company')
        .sort(byActiveFirst)
        .map((place) => (
          <ContactCompanyCard
            key={place.id}
            companyName={place.name}
            deactivatedLabel={place.deactivated ? text('contactDeactivated') : null}
            actions={placeActions(place, { labelled: true })}
          />
        ))}
      <p className="text-sm text-gray-600">{text('contactNotRegisteredText')}</p>
    </div>
  );

  return (
    <RecordLayout
      side={
        <>
          <RecordIdentity
            back={{ label: owner.name, onClick: () => navigate(ownerPath(owner)) }}
            picture={<EmployeeAvatar employee={person} size={48} />}
            title={name}
            // The company is already on the back button: under the name, the position.
            subtitle={person.position || text('noPosition')}
          >
            {person.leadStatus === 'ambassadeur' && (
              <div className="mt-2">
                <AmbassadorBadge />
              </div>
            )}
            <QuickActions
              actions={[
                { key: 'note', label: text('actionNote'), icon: Pencil, onClick: () => openForm('note') },
                { key: 'email', label: text('actionEmail'), icon: Mail, onClick: () => openForm('email') },
                callAction({ label: text('actionCall'), people: [me], onNone: () => toast.info(text('noPhone')) }),
                { key: 'task', label: text('actionTask'), icon: ClipboardListIcon, onClick: () => add('task') },
                { key: 'event', label: text('actionMeeting'), icon: CalendarDaysIcon, onClick: () => openForm('event') },
              ]}
            />
          </RecordIdentity>
          {/* What comes next with them, as on a company's page; nothing planned: no card. */}
          {next && <NextActionCard label={text('nextAction')} title={next.title} detail={next.detail} {...nextButtons} />}
          <RecordSection
            title={text('information')}
            action={
              editInformation && (
                <CardIconButton label={text('contactEditInformation')} icon={<Pencil wh={18} color="#374151" />} onClick={editInformation} />
              )
            }
          >
            <RecordField label={text('fieldPosition')}>{person.position}</RecordField>
            <RecordField label={text('fieldEmail')}>
              {person.email && (
                <CopyToClipboard text={person.email} tooltipText={text('contactCopyEmail')} toastText={text('contactEmailCopied')}>
                  {person.email}
                </CopyToClipboard>
              )}
            </RecordField>
            <RecordField label={text('fieldPhone')}>
              {person.phone && (
                <CopyToClipboard text={person.phone} tooltipText={text('contactCopyPhone')} toastText={text('contactPhoneCopied')}>
                  {person.phone}
                </CopyToClipboard>
              )}
            </RecordField>
            <RecordField label={text('fieldLead')}>{person.leadStatus && text(`lead_${person.leadStatus}`)}</RecordField>
          </RecordSection>
          <RecordSection title={text(groups ? 'placesWithGroups' : 'places', { count: memberships.length })}>
            {memberships.map((place) => (
              <div key={`${place.kind}-${place.id}`} className="flex items-center gap-3 border-b border-gray-100 py-2.5 last:border-0">
                <button
                  type="button"
                  onClick={() => navigate(ownerPath(place))}
                  className={`min-w-0 flex-1 text-left ${place.deactivated ? 'opacity-50' : ''}`}
                >
                  <ListIdentity
                    icon={place.kind === 'group' ? NetworkIcon : Building2Icon}
                    name={place.name}
                    detail={placeDetail(place)}
                    nameClassName="hover:underline"
                  />
                </button>
                {placeActions(place)}
                {/* A place deactivated is greyed, its settings kept for its return. */}
                {place.kind === 'company' && place.deactivated && (
                  <Button
                    variant="secondary"
                    aria-label={text('contactReactivateLabel', { name, company: place.name })}
                    onClick={() => reactivateAt(place)}
                  >
                    {text('contactReactivate')}
                  </Button>
                )}
              </div>
            ))}
          </RecordSection>
        </>
      }
    >
      <RecordTabs
        tabs={[
          { id: 'activity', label: text('tab_activity') },
          { id: 'companies', label: text('contactTabCompanies') },
        ]}
        activeTab={tab}
        onChange={setTab}
      />
      {tab === 'activity' && (
        <RecordActivity
          items={items}
          onAdd={add}
          onOpen={openItem}
          // What a member does in Jobbiz: their own actions, from the api to come.
          showClient={Boolean(person.account)}
        />
      )}
      {tab === 'companies' &&
        (person.account ? (
          <div className="flex flex-col gap-2 pb-6">
            {accountPlaces.map((place) => (
              <MemberCompanyCard
                key={place.id}
                companyId={place.id}
                companyName={place.name}
                memberId={person.id}
                email={person.email}
                role={place.role}
                // A deactivated place is not re-invited (canInviteAt): no pending invitation, no resend.
                pending={place.account === 'pending' && !place.deactivated}
                deactivatedLabel={place.deactivated ? text('contactDeactivated') : null}
                onEdit={editMemberAt(place, EDIT_SCOPES.company)}
                actions={placeActions(place, { labelled: true })}
              />
            ))}
          </div>
        ) : (
          notOnJobbiz
        ))}
      <ConfirmDialog
        open={Boolean(removal)}
        busy={removalBusy}
        title={removal ? text('contactRemoveTitle', { name, company: removal.name }) : ''}
        description={removal ? text(removal.account ? 'contactRemoveAccountText' : 'contactRemoveContactText') : ''}
        cancelLabel={text('contactRemoveCancel')}
        confirmLabel={text('contactRemoveConfirm')}
        onCancel={() => setRemoval(null)}
        onConfirm={confirmRemoval}
      />
      <ContactEditDialog
        open={editingContact}
        contactId={contactId}
        person={person}
        onClose={() => setEditingContact(false)}
        onSaved={() => {
          setEditingContact(false);
          load();
        }}
      />
    </RecordLayout>
  );
};

export default ContactPage;
